import { gql } from '@apollo/client'
import { ArrowRightIcon, BodyText, Button, Heading } from '@farol-ds/react'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  COMPONENT_CLASS,
  COMPONENT_NAME,
  FEATURE_CLASS,
  FEATURE_NAME,
  GA4ClickEvent,
  GA4CustomComponentRenderedEvent,
  GA4FeatureUseEvent,
  REQUIRED_USER_STATUS,
  SELECTED_OPTION,
} from '@jusbrasil-web/shared-ga4-events'
import { useEffect, useMemo } from 'react'

import { useMetrics } from '../../../hooks'
import { DocPopoverSummaryQuery } from './__generated__/doc-popover-summary.graphql'
import styles from './styles.module.scss'

export type DocPopoverSummaryProps = {
  query: DocPopoverSummaryQuery
  context: string
}

export function DocPopoverSummary(props: DocPopoverSummaryProps) {
  const {
    query: {
      root: { document: doc },
    },
    context,
  } = props
  const { ref: dispatcherRef, sendMetrics } = useMetrics<HTMLDivElement>()

  const { sendGA4Event } = useGA4Dispatcher()
  const ga4Props = {
    componentClass: COMPONENT_CLASS.RELATED_DOCUMENT_POPOVER,
    componentName: COMPONENT_NAME.RELATED_SUMMARY,
  }

  // caught by the error boundary
  if (!doc) throw new Error('Document not found')

  const isInTopicContext = context === 'TOPIC'
  const sourceComponent = isInTopicContext ? 'RelatedSumula' : ''

  const defaultProps = useMemo(() => {
    return {
      from_component: 'SummaryPopover',
      source_component: sourceComponent,
      topic_type: isInTopicContext ? 'ATO_NORMATIVO' : '',
      metadata: {
        popover: { entity: 'DOCUMENTO', docId: doc.docId, artifact: doc.artifact },
      },
    }
  }, [doc, sourceComponent, isInTopicContext])

  useEffect(() => {
    sendGA4Event(
      new GA4CustomComponentRenderedEvent({
        ...ga4Props,
      })
    )

    const eventName = isInTopicContext ? 'Topic.PopoverOpened' : 'Doc.PopoverOpened'
    sendMetrics(eventName, defaultProps)
    // eslint-disable-next-line
  }, [])

  const trackSummaryLinkClick = () => {
    sendGA4Event(
      new GA4ClickEvent({
        ...ga4Props,
        selectedOption: SELECTED_OPTION.FULL_DOCUMENT_ACCESS,
        resourceId: `${doc.artifact}:${doc.docId}`,
      })
    )
    const eventName = isInTopicContext ? 'Topic.PopoverClicked' : 'Doc.PopoverClicked'
    sendMetrics(eventName, defaultProps)
  }

  const onCopy = () => {
    sendGA4Event(
      new GA4FeatureUseEvent({
        ...ga4Props,
        featureClass: FEATURE_CLASS.COPY_CONTENT,
        featureName: FEATURE_NAME.COPY_JURISPRUDENCE_CONTENT,
        resourceId: `${doc.artifact}:${doc.docId}`,
        requiredUserStatus: REQUIRED_USER_STATUS.UNAUTHENTICATED_USER,
      })
    )
  }

  return (
    <div className={styles.contentRoot} ref={dispatcherRef}>
      <Heading size="sm">{doc.title}</Heading>

      <BodyText size="md" onCopy={onCopy} asChild>
        <div dangerouslySetInnerHTML={{ __html: doc.summaryPrecedentStatement ?? '' }} />
      </BodyText>

      {doc.url && (
        <div className={styles.contentFooter}>
          <Button data-testid="doc-popover-summary-button" rightIcon={<ArrowRightIcon />} asChild>
            <a
              href={doc.url}
              onClick={trackSummaryLinkClick}
              target="_blank"
              rel="noopener noreferrer"
            >
              Ver súmula completa
            </a>
          </Button>
        </div>
      )}
    </div>
  )
}

DocPopoverSummary.query = gql`
  query DocPopoverSummary($artifact: String!, $docId: NumericID!) {
    root {
      document: documentByNumericID(artifact: $artifact, docId: $docId) {
        title
        summaryPrecedentStatement
        url
        docId
        artifact
      }
    }
  }
`
